/**
 * Error State Component
 * Displays error messages with optional retry action
 */

import React from "react";
import { Box, Typography, Button, Alert, AlertTitle } from "@mui/material";
import { Refresh as RefreshIcon, Error as ErrorIcon } from "@mui/icons-material";
import { getApiErrorMessage as getErrorMessageFromApi } from "@/utils/errors";

export interface ErrorStateProps {
  /** Error title */
  title?: string;
  /** Error message */
  message: string;
  /** Retry callback */
  onRetry?: () => void;
  /** Retry button label */
  retryLabel?: string;
  /** Display variant */
  variant?: "inline" | "page" | "alert";
  /** Minimum height for page variant */
  minHeight?: number | string;
}

/**
 * Extract error message from various error formats
 * @deprecated Use getApiErrorMessage from @/utils/errors instead
 */
export const getErrorMessage = (error: unknown): string => {
  return getErrorMessageFromApi(error, "An unexpected error occurred");
};

const ErrorStateComponent: React.FC<ErrorStateProps> = ({
  title = "Error",
  message,
  onRetry,
  retryLabel = "Try Again",
  variant = "inline",
  minHeight = 300,
}) => {
  if (variant === "alert") {
    return (
      <Alert
        severity="error"
        action={
          onRetry ? (
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
            >
              {retryLabel}
            </Button>
          ) : undefined
        }
      >
        <AlertTitle>{title}</AlertTitle>
        {message}
      </Alert>
    );
  }

  if (variant === "page") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight,
          py: 6,
          px: 3,
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: "error.lighter",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <ErrorIcon sx={{ fontSize: 40, color: "error.main" }} />
        </Box>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            color: "text.primary",
            fontWeight: 600
          }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            maxWidth: 400,
            mb: onRetry ? 3 : 0
          }}>
          {message}
        </Typography>
        {onRetry && (
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
          >
            {retryLabel}
          </Button>
        )}
      </Box>
    );
  }

  // inline variant (default)
  return (
    <Alert severity="error" sx={{ mb: 3 }}>
      <AlertTitle>{title}</AlertTitle>
      <Typography variant="body2">{message}</Typography>
      {onRetry && (
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          sx={{ mt: 1 }}
        >
          {retryLabel}
        </Button>
      )}
    </Alert>
  );
};

/**
 * Memoized ErrorState - prevents unnecessary re-renders
 */
export const ErrorState = React.memo(ErrorStateComponent);

export default ErrorState;
